/**
 * Distribution Actions Hook
 *
 * This hook handles action operations for the distribution feature.
 * It follows the Single Responsibility Principle by focusing only on actions.
 *
 * @module useDistributionActions
 */

import {useCallback} from 'react';
import {useDistributionApplicationService} from './useDistributionApplicationService';
import {useDistributionStore} from '../store/distributionStore';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {CreateDeliveryForm} from '../models/distribution.model';

/**
 * Hook for distribution action operations
 *
 * @returns Object containing action functions
 */
export const useDistributionActions = () => {
  const applicationService = useDistributionApplicationService();
  const {removeTransaction} = useDistributionStore();
  const {success: showSuccess} = useNotification();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  const createDelivery = useCallback(async (deliveryData: CreateDeliveryForm) => {
    try {
      const result = await applicationService.createDelivery(deliveryData);
      showSuccess(result.message || 'تم إنشاء التوزيع بنجاح');
      return result;
    } catch (error: unknown) {
      errorHandlingService.handleError(error);
      throw error;
    }
  }, [applicationService, showSuccess, errorHandlingService]);

  const deleteTransaction = useCallback(async (transactionId: string) => {
    try {
      await applicationService.deleteTransaction(transactionId);
      removeTransaction(transactionId);
      showSuccess('تم حذف المعاملة بنجاح');
      return true;
    } catch (error: unknown) {
      errorHandlingService.handleError(error); // For notifications/logging
      return false;
    }
  }, [applicationService, removeTransaction, showSuccess, errorHandlingService]);

  const updateDelivery = useCallback(async (id: string, updateData: Record<string, unknown>) => {
    try {
      const result = await applicationService.updateDelivery(id, updateData);
      showSuccess('تم تحديث التوزيع بنجاح');
      return result;
    } catch (error: unknown) {
      errorHandlingService.handleError(error);
      throw error;
    }
  }, [applicationService, showSuccess, errorHandlingService]);

  return {
    createDelivery,
    deleteTransaction,
    updateDelivery
  };
};
